// ////////////////////
// Assist API Type Definitions
// ////////////////////

/** Assist機能の種別 */
// ////////////////////
// Utility Functions
// ////////////////////

import { WriterType } from "./writerTypeIndicator";

export enum AssistFunctionType {
  /** 原稿感想生成 */
  FEEDBACK_MANUSCRIPT = "FEEDBACK_MANUSCRIPT",
  /** 応募先マッチング */
  MATCHING_COMPANY = "MATCHING_COMPANY",
  /** キャッチコピー生成 */
  GENERATE_CATCHCOPY = "GENERATE_CATCHCOPY",
  /** あらすじ生成 */
  GENERATE_SUMMARY = "GENERATE_SUMMARY",
  /** 企画（theme）レビュー */
  FEEDBACK_THEME = "FEEDBACK_THEME",
}

/** Assist API用の作家タイプ（16種類） */
export enum AssistWriterType {
  ELAH = "ELAH", // 外向・論理・突発・正直
  ELAS = "ELAS", // 外向・論理・突発・誠実
  ELPH = "ELPH", // 外向・論理・完璧・正直
  ELPS = "ELPS", // 外向・論理・完璧・誠実
  EMAH = "EMAH", // 外向・感情・突発・正直
  EMAS = "EMAS", // 外向・感情・突発・誠実
  EMPH = "EMPH", // 外向・感情・完璧・正直
  EMPS = "EMPS", // 外向・感情・完璧・誠実
  ILAH = "ILAH", // 内向・論理・突発・正直
  ILAS = "ILAS", // 内向・論理・突発・誠実
  ILPH = "ILPH", // 内向・論理・完璧・正直
  ILPS = "ILPS", // 内向・論理・完璧・誠実
  IMAH = "IMAH", // 内向・感情・突発・正直
  IMAS = "IMAS", // 内向・感情・突発・誠実
  IMPH = "IMPH", // 内向・感情・完璧・正直
  IMPS = "IMPS", // 内向・感情・完璧・誠実
}

/** アシスタント猫の感想モード */
export enum FeedbackManuscriptMode {
  NORMAL = "NORMAL",
  DRY = "DRY",
}

/** アシスタント猫のあらすじモード */
export enum GenerateSummaryMode {
  WAKUWAKU = "WAKUWAKU",
  FUMUFUMU = "FUMUFUMU",
}

/** アシストパネルの表示コンテキスト（表示中ページに応じた依頼メニューの出し分け） */
export type AssistPanelContext = "manuscript" | "theme";

/** クライアントプラットフォーム */
export enum ClientPlatform {
  WEB = "WEB",
  IOS = "IOS",
  ANDROID = "ANDROID",
}

/** Assistの処理結果 */
export interface AssistResult {
  /** AIが生成したメッセージ */
  message?: string;
  /** AIが生成したHTML */
  html?: string;
}

/** Assist処理ログ */
export interface Assist {
  /** ユーザーID */
  userId: string;
  /** 作品ID */
  novelId: string;
  /** アシスト機能種別 */
  functionType: AssistFunctionType;
  /** アシストID（UUIDv7） */
  assistId: string;
  /** 作家タイプ */
  writerType?: AssistWriterType;
  /** 感想モード */
  feedbackManuscriptMode?: FeedbackManuscriptMode;
  /** あらすじモード */
  generateSummaryMode?: GenerateSummaryMode;
  /** 依頼タイトル */
  requestTitle?: string;
  /** アシスト結果 */
  result?: AssistResult;
  /** 処理完了フラグ */
  completed: boolean;
  /** エラーフラグ */
  isError?: boolean;
  /** 作成日時（ISO文字列） */
  createdAt: string;
  /** 更新日時（ISO文字列） */
  updatedAt: string;
}

// ////////////////////
// Request Input Types
// ////////////////////

/** 原稿感想生成リクエスト */
export interface CreateFeedbackManuscriptInput {
  /** 作品ID */
  novelId: string;
  /** 原稿キーの配列 */
  manuscriptKeys: string[];
  /** 作家タイプ */
  writerType: AssistWriterType;
  /** モード */
  feedbackManuscriptMode?: FeedbackManuscriptMode;
  /** 依頼タイトル */
  requestTitle: string;
  /** プラットフォーム */
  platform?: ClientPlatform;
}

/** 応募先マッチングリクエスト */
export interface CreateMatchingCompanyInput {
  /** 作品ID */
  novelId: string;
  /** 原稿キーの配列 */
  manuscriptKeys: string[];
  /** 作家タイプ */
  writerType: AssistWriterType;
  /** 依頼タイトル */
  requestTitle: string;
  /** プラットフォーム */
  platform?: ClientPlatform;
}

/** キャッチコピー生成リクエスト */
export interface CreateGenerateCatchcopyInput {
  /** 作品ID */
  novelId: string;
  /** 原稿キーの配列 */
  manuscriptKeys: string[];
  /** 作家タイプ */
  writerType: AssistWriterType;
  /** 依頼タイトル */
  requestTitle: string;
  /** プラットフォーム */
  platform?: ClientPlatform;
}

/** あらすじ生成リクエスト */
export interface CreateGenerateSummaryInput {
  /** 作品ID */
  novelId: string;
  /** 原稿キーの配列 */
  manuscriptKeys: string[];
  /** 作家タイプ */
  writerType: AssistWriterType;
  /** あらすじモード */
  generateSummaryMode?: GenerateSummaryMode;
  /** 依頼タイトル */
  requestTitle: string;
  /** プラットフォーム */
  platform?: ClientPlatform;
}

/** 企画（theme）レビューリクエスト */
export interface CreateFeedbackThemeInput {
  /** 作品ID */
  novelId: string;
  /** 作家タイプ */
  writerType: AssistWriterType;
  /** 依頼タイトル */
  requestTitle: string;
  /** プラットフォーム */
  platform?: ClientPlatform;
}

/** アシストログ取得リクエスト */
export interface GetAssistLogInput {
  /** 作品ID */
  novelId: string;
  /** アシストID */
  assistId: string;
  /** アシスト機能種別 */
  functionType: AssistFunctionType;
}

/** アシストログ一覧取得リクエスト */
export interface ListAssistLogInput {
  /** 作品ID */
  novelId: string;
  /** アシスト機能種別（省略可） */
  functionType?: AssistFunctionType;
}

/**
 * 既存のWriterTypeからAssistWriterTypeに変換
 * @param writerType 作家診断結果のWriterType
 * @returns AssistWriterType
 * @throws Error 無効な作家タイプの場合
 */
export function writerTypeToAssistWriterType(writerType: WriterType): AssistWriterType {
  // バリデーションを実行してから変換
  if (!isValidAssistWriterType(writerType.tendency)) {
    throw new Error(
      `Invalid writer type tendency: ${writerType.tendency}. Expected one of: ${Object.values(AssistWriterType).join(
        ", "
      )}`
    );
  }

  // 既存システムでは"ELAS"などの文字列が tendency に格納されている
  return writerType.tendency as AssistWriterType;
}

/**
 * AssistWriterTypeの文字列が有効かチェック
 * @param writerTypeString チェック対象の文字列
 * @returns 有効な場合true
 */
export function isValidAssistWriterType(writerTypeString: string): writerTypeString is AssistWriterType {
  return Object.values(AssistWriterType).includes(writerTypeString as AssistWriterType);
}
